package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.usecase.GetCharacterByIdUseCase
import fr.manyverse.app.domain.usecase.TouchMemberCharacterUseCase
import fr.manyverse.app.domain.usecase.UpdateCharacterSheetUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Récupère et laisse le MJ modifier la fiche d'un personnage de joueur pendant la partie.
 *
 * **Lecture** : la liste de personnages observée par [ActiveGameViewModel] n'est rafraîchie que
 * lorsque le flux Realtime `game_table_members` émet ; le contenu du `sheet` (compétences,
 * caractéristiques, jauges) n'étant pas synchronisé par Realtime, il faut le re-fetcher à chaque
 * ouverture de la fiche. `getCharacterById` ne passe pas par le cache et tape directement Supabase.
 *
 * **Écriture (jauges)** : le MJ ajuste les jauges via +/−. Chaque pas persiste la fiche complète
 * ([UpdateCharacterSheetUseCase]) puis « touche » la ligne de membre du MJ
 * ([TouchMemberCharacterUseCase]) : ce bump émet un changement `game_table_members` qui pousse le
 * joueur propriétaire à re-fetcher son personnage (donc la jauge à jour).
 */
class GmPlayerSheetViewModel(
    private val characterId: String,
    private val tableId: String,
    private val gmUserId: String,
    private val getCharacterByIdUseCase: GetCharacterByIdUseCase,
    private val updateCharacterSheetUseCase: UpdateCharacterSheetUseCase,
    private val touchMemberCharacterUseCase: TouchMemberCharacterUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _sheetState = MutableStateFlow<AsyncUiState<CharacterSheet>>(AsyncUiState.Loading)
    val sheetState: StateFlow<AsyncUiState<CharacterSheet>> = _sheetState.asStateFlow()

    private val _updateState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateState: StateFlow<AsyncUiState<Unit>> = _updateState.asStateFlow()

    init {
        load()
    }

    fun load() {
        launchAsyncMapped(
            state = _sheetState,
            block = { getCharacterByIdUseCase(characterId) },
            transform = { it?.sheet ?: CharacterSheet() }
        )
    }

    fun incrementGauge(gaugeId: String) = stepGauge(gaugeId, up = true)

    fun decrementGauge(gaugeId: String) = stepGauge(gaugeId, up = false)

    fun resetUpdateState() = resetState(_updateState)

    private fun stepGauge(gaugeId: String, up: Boolean) {
        val current = (_sheetState.value as? AsyncUiState.Success)?.data ?: return
        val gauges = current.gauges.map { gauge ->
            if (gauge.id != gaugeId) return@map gauge
            val delta = if (up) gauge.increment else -gauge.increment
            gauge.copy(current = (gauge.current + delta).coerceIn(gauge.min, gauge.max))
        }
        val newSheet = current.copy(gauges = gauges)

        // Affichage optimiste immédiat côté MJ.
        _sheetState.value = AsyncUiState.Success(newSheet)

        viewModelScope.launch {
            _updateState.value = AsyncUiState.Loading
            updateCharacterSheetUseCase(characterId, newSheet)
                .onSuccess {
                    _updateState.value = AsyncUiState.Success(Unit)
                    // Notifie le joueur propriétaire via un changement `game_table_members`.
                    touchMemberCharacterUseCase(tableId, gmUserId)
                }
                .onFailure { exception ->
                    _updateState.value = AsyncUiState.Error(exception.toUserMessage())
                    // Sans message, le MJ verrait seulement la jauge revenir en arrière
                    errorNotifier.notify(exception)
                    // Revient à l'état réel en base si l'écriture a échoué.
                    load()
                }
        }
    }
}
